/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import type {FeatureCategory} from '@dms/api/new-vehicle';

type Translate = (key: string, defaultValue: string) => string;

const labels = {
  availability: {available: 'Available', unavailable: 'Unavailable'},
  commercialAvailability: {
    available: 'Available',
    orderable: 'Orderable',
    preview: 'Preview',
    soldOut: 'Sold out',
    sold_out: 'Sold out',
  },
  itemType: {
    variant: 'Variant',
    colour: 'Colour',
    upholstery: 'Upholstery',
    feature: 'Feature',
    package: 'Package',
  },
  inclusion: {STANDARD: 'Standard', OPTIONAL: 'Optional'},
  ruleType: {requires: 'Requires', excludes: 'Excludes', autoAdd: 'Adds automatically'},
  featureCategory: {
    interior: 'Interior',
    infotainment_communication: 'Infotainment & communication',
    safety_assistance: 'Safety & assistance',
    exterior: 'Exterior',
    wheels_tyres: 'Wheels & tyres',
    mechanical: 'Mechanical',
    load_space: 'Load space',
    other: 'Other',
  } satisfies Record<FeatureCategory, string>,
  featureKind: {
    option: 'Factory option',
    wheel: 'Wheel set',
    accessory: 'Dealer-fit accessory',
    colour: 'Colour',
    upholstery: 'Upholstery',
  },
  modelYearStatus: {draft: 'Draft', active: 'Published', archived: 'Archived'},
  reasonCode: {
    not_in_trim: 'Not available on this trim level',
    package_only: 'Only available inside a package',
    excluded: 'Cannot be combined with your selection',
    trim_not_orderable: 'Trim level is not orderable',
    variant_disabled: 'Not available with this engine',
    not_available_for_trim: 'Not available for this configuration',
    unknown_item: 'No longer in the catalogue',
    requires_unavailable: 'Requires another item',
    not_available_with_colour: 'Not available with the selected colour',
    wheel_set_conflict: 'Only one wheel set per vehicle',
    standard_inclusion: 'Included as standard on this trim level',
  },
  changeKind: {percentage: 'Percent', amount: 'Amount', fillDown: 'Fill down'},
  direction: {up: 'Increase', down: 'Decrease'},
  diffPath: {
    modelYearId: 'Model year',
    trimId: 'Trim level',
    variantId: 'Variant',
    colourId: 'Colour',
    upholsteryId: 'Upholstery',
    featureIds: 'Equipment',
    packageIds: 'Packages',
  },
  adjustmentScope: {
    all: 'Whole pricelist',
    variant: 'Variants',
    trim: 'Trim levels',
    colour: 'Colours',
    upholstery: 'Upholstery',
    feature: 'Equipment',
    package: 'Packages',
    trimFeature: 'Trim equipment prices',
    conditionalPrice: 'Conditional prices',
    variantPriceOverride: 'Variant price overrides',
    colourTrim: 'Trim colour prices',
    packageTrim: 'Trim package prices',
    mixed: 'Several item types',
  },
  pricelistChangeOperation: {
    price: 'Price change',
    created: 'Added',
    removed: 'Removed',
    madeStandard: 'Made standard',
    madeOptional: 'Made optional',
  },
  pricelistVersionOrigin: {
    publish: 'Published',
    adjustment: 'Price adjustment',
    itemChange: 'Items added or removed',
  },
  discountKind: {percent: 'Percentage', amount: 'Amount'},
  discountBasis: {net: 'Net', gross: 'Gross'},
  packageStatus: {draft: 'Draft', active: 'Active', archived: 'Archived'},
  orderSource: {customer: 'Customer order', stock: 'Dealer stock', external: 'External order'},
  statusLogKind: {manual: 'Manual update', integration: 'Importer update'},
  orderStatus: {
    configured: 'Configured',
    ordered: 'Ordered',
    in_production: 'In production',
    in_transit: 'In transit',
    arrived: 'Arrived',
    ready_for_handover: 'Ready for handover',
    handed_over: 'Handed over',
    cancelled: 'Cancelled',
  },
  priceValidity: {valid: 'Price valid', expired: 'Price expired'},
  publicationStatus: {
    active: 'Published',
    online: 'Published',
    draft: 'Draft',
    archived: 'Archived',
  },
  fitStage: {factory: 'Factory fitted', dealer: 'Dealer fitted'},
} as const;

export type NewVehicleLabelGroup = keyof typeof labels;

const humanise = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/^./, (letter) => letter.toUpperCase());

export const getNewVehicleEnumLabel = (
  group: NewVehicleLabelGroup,
  value: string,
  t: Translate
) => {
  const entry = Object.entries(labels[group]).find(
    ([key]) => key.toLowerCase() === value.toLowerCase()
  );
  const key = entry?.[0] ?? value;
  return t(`entity.newVehicle.enums.${group}.${key}`, entry?.[1] ?? humanise(value));
};

/** Configurator display order of the closed BE enum. */
export const FEATURE_CATEGORIES = [
  'interior',
  'infotainment_communication',
  'safety_assistance',
  'exterior',
  'wheels_tyres',
  'mechanical',
  'load_space',
  'other',
] as const satisfies readonly FeatureCategory[];
type UnlistedFeatureCategory = Exclude<FeatureCategory, (typeof FEATURE_CATEGORIES)[number]>;
// Fails typecheck when the BE enum gains a value missing from the display order.
const _allFeatureCategoriesListed: [UnlistedFeatureCategory] extends [never] ? true : never = true;

export const getFeatureCategoryOptions = (t: Translate) =>
  FEATURE_CATEGORIES.map((value) => ({
    value,
    label: getNewVehicleEnumLabel('featureCategory', value, t),
  }));

export const getNewVehicleDiffPathLabel = (value: string, t: Translate) => {
  const fallback = Object.entries(labels.diffPath).find(([key]) => key === value)?.[1];
  return fallback
    ? t(`entity.newVehicle.enums.diffPath.${value}`, fallback)
    : t('entity.newVehicle.labels.unknownSelection', 'Unavailable selection');
};
